import { FormControl, FormGroup } from '@angular/forms';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { MultiSelectSelectAllChangeEvent } from 'primeng/multiselect';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { Hub } from '@qwyk/models';
import { LocalStorageService } from '@qwyk/core';
import { AuthenticationFacade } from '@qwyk/hub/authentication';
import { PortalTeamsFacade } from '@qwyk/hub/administration/data-access/portal-teams';
import { CustomerUsersFacade } from '@qwyk/hub/administration/data-access/customer-users';

import { CustomerUserImpersonateModalComponent } from '../customer-user-impersonate-modal/customer-user-impersonate-modal.component';

@Component({
    selector: 'qwyk-customer-users-list',
    templateUrl: './customer-users-list.component.html',
    styleUrls: ['./customer-users-list.component.scss'],
    providers: [DialogService],
})
export class CustomerUsersListComponent implements OnInit, OnDestroy {

    private modal = inject(DialogService);
    private translate = inject(TranslateService);
    private localStorage = inject(LocalStorageService);
    private customerUsers = inject(CustomerUsersFacade);
    private portalTeamsFacade = inject(PortalTeamsFacade);
    private authentication = inject(AuthenticationFacade);

    public customers$ = this.portalTeamsFacade.allPortalTeams$;
    public loadingCustomers$ = this.portalTeamsFacade.loading$;
    public loading$ = this.customerUsers.loading$;
    public customerUsers$ = this.customerUsers.allCustomerUsers$;
    public pagination$ = this.customerUsers.pagination$;
    public isMagayaNetwork$ = this.authentication.isMagayaNetwork$;

    public isTableView = true;
    public pageSize = 25;
    public selectAll = false;
    public searchForm = new FormGroup({
        name: new FormControl(null),
        email: new FormControl(null),
        team_id: new FormControl(null),
        tag: new FormControl(null),
        sort: new FormControl<string|string[]>('name'),
        sort_dir: new FormControl('asc'),
    });
    public columns: SelectItem[] = [
        {
            label: 'common.name',
            value: {
                field: 'name',
                header: 'common.name',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'email',
            },
            disabled: true,
        },
        {
            label: 'common.email',
            value: {
                field: 'email',
                header: 'common.email',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'email',
            },
        },
        {
            label: 'portal.registration.page1.label-username',
            value: {
                field: 'username',
                header: 'portal.registration.page1.label-username',
                default: true,
                filter: true,
                sort: false,
                sort_key: 'username',
            },
        },
        {
            label: 'common.customer',
            value: {
                field: 'team_name',
                header: 'common.customer',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'customer_teams.name',
            },
        },
        {
            label: 'portal.shipment.search.placeholder.company',
            value: {
                field: 'company_name',
                header: 'portal.shipment.search.placeholder.company',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'company_name',
            },
        },
        {
            label: 'portal.settings.profile.job-title',
            value: {
                field: 'job_title',
                header: 'portal.settings.profile.job-title',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'job_title',
            },
        },
        {
            label: 'portal.cargo-items.columns.location',
            value: {
                field: 'location',
                header: 'portal.cargo-items.columns.location',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'location',
            },
        },
        {
            label: 'common.country',
            value: {
                field: 'country.short_name',
                header: 'common.country',
                default: false,
                filter: false,
                sort: false,
                sort_key: null,
                nested: ['country', 'short_name'],
            },
        },
        {
            label: 'hub.shipment.sections.instructions.subsections.parties-fields.phone-number',
            value: {
                field: 'phone_number',
                header: 'hub.shipment.sections.instructions.subsections.parties-fields.phone-number',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'phone_number',
            },
        },
        {
            label: 'common.sort_options.created_at',
            value: {
                field: 'created_at',
                header: 'common.sort_options.created_at',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'created_at',
            },
        },
        {
            label: 'hub.administration.portal-users.last-login-at',
            value: {
                field: 'last_login_at',
                header: 'hub.administration.portal-users.last-login-at',
                default: false,
                filter: false,
                sort: true,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'hub.administration.portal-users.approved-by',
            value: {
                field: 'approved_by',
                header: 'hub.administration.portal-users.approved-by',
                default: false,
                filter: false,
                sort: false,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'hub.administration.portal-users.marketing-mails',
            value: {
                field: 'marketing_optin',
                header: 'hub.administration.portal-users.marketing-mails',
                default: false,
                filter: false,
                sort: false,
                sort_key: 'last_login_at',
            },
        },
        {
            label: 'common.active',
            value: {
                field: 'deactivated',
                header: 'common.active',
                default: true,
                filter: true,
                sort: true,
                sort_key: 'deactivated',
            },
        },
    ];

    private _selectedColumns = [];
    private _ref: DynamicDialogRef | undefined;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public get selectedColumns(): any[] {
        return this._selectedColumns;
    }

    public set selectedColumns(value) {
        this._selectedColumns = value;
        this.saveTableCols();
    }

    ngOnInit(): void {
        this.translateColumns();
        this.restoreTableCols();
        this.portalTeamsFacade.loadPortalTeams({ page: 1, limit: this.pageSize });
    }

    public loadCustomerUsersLazy(event: TableLazyLoadEvent) {
        setTimeout(() => {
            let page = 1;

            this.pageSize =
                !event.rows || event.rows < 25 ? this.pageSize : event.rows;

            if (event.first && event.rows) {
                page = event.first / event.rows + 1;
            }
            if (event.sortField) {
                this.searchForm.patchValue({
                    sort: event.sortField || 'name',
                    sort_dir: event.sortOrder === 1 ? 'asc' : 'desc',
                });
            }

            this.search(page);
        }, 50);
    }

    public search(page = 1) {
        const searchValue = this.searchForm.value;
        Object.keys(searchValue).forEach(key => {
            if (searchValue[key] === null) {
                delete searchValue[key];
            }
        });
        const query = {
            ...searchValue,
            pageSize: this.pageSize,
            page,
        };

        this.customerUsers.loadCustomerUsers(query);
    }

    public resetSearch() {
        this.searchForm.reset();
        this.search(1);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public onTableColReorder(e): void {
        this.saveTableCols();
    }

    public onSelectAllChange(event : MultiSelectSelectAllChangeEvent) {
        this.selectAll = event.checked;
        if (event.checked) {
            this.selectedColumns = this.columns.map(col => col.value);
        } else {
            this.selectedColumns = this.columns
                .filter(col => col.value.default)
                .map(col => col.value);
        }
        this.saveTableCols();
    }

    public formatExport(value: { field: string; data: any }) {
        if(value.field === 'deactivated'){
            return value.data ? 'No' : 'Yes'
        }
        return value.data;
    }

    onImpersonateUser(user: Hub.CustomerUser) {
        this._ref = this.modal.open(CustomerUserImpersonateModalComponent, {
            position: 'center',
            showHeader: false,
            closable: false,
            width: '30%',
            contentStyle: {
                'padding': 0
            },
            data: {
                user
            }
        });
    }

    public ngOnDestroy(): void {
        if (this._ref) {
            this._ref.close();
        }
    }

    public onSearchCustomers(term: string): void {
        this.portalTeamsFacade.loadPortalTeams({ name: term })
    }

    public onClearCustomersSearch(): void {
        this.portalTeamsFacade.loadPortalTeams({ page: 1, limit: this.pageSize });
    }

    private translateColumns(): void {
        this.columns.forEach(col => {
            col.label = this.translate.instant(col.label);
            col.value.header = this.translate.instant(col.value.header);
        });
    }

    /**
     * Saves table column layout to localstorage.
     * @private
     */
    private saveTableCols(): void {
        this.localStorage.setObject(
            'administration.portal-users.table-layout.v3',
            this._selectedColumns
        );
    }

    /**
     * Restores table column layout from localstorage.
     * @private
     */
    private restoreTableCols(): void {
        let columns = null;

        try {
            columns = this.localStorage.getObject(
                'administration.portal-users.table-layout.v3'
            );
        } catch {
            /* empty */
        }

        if (!columns) {
            columns = this.columns
                .filter(e => e.value.default)
                .map(e => e.value);
        }
        this._selectedColumns = columns;
    }
}
